import { Preferences } from '@beyond-js/ui';
import { Locale } from '@cdn/i18n';

interface Specs {
	/** The device's own storage; without it the values last for this page */
	storage?: Storage;
	/** The product default of the language: the entry address's, the tab's, the browser's, or English */
	locale: string;
	/** The element that carries `data-beyond-mode` and `lang`; the document's by default */
	root?: HTMLElement;
}

/**
 * The person's language and appearance on this device (decision D07): the family's `Preferences` under this
 * application's key, restored for the first paint. The account's values replace them on arrival.
 *
 * The two keys of earlier versions (`beyond.cdn.locale`, `beyond.cdn.mode`) are carried over once, as a choice
 * made on this device, and removed; the device copy never holds an account identifier.
 */
export class Device {
	static readonly key = 'beyond-cdn';
	static readonly #earlier = { locale: 'beyond.cdn.locale', appearance: 'beyond.cdn.mode' };

	static open({ storage, locale, root }: Specs): Preferences {
		const preferences = new Preferences({ key: Device.key, fallback: { appearance: 'system', locale }, storage: storage ?? null, root });
		preferences.restore();
		Device.#carry(preferences, storage);
		return preferences;
	}

	static #carry(preferences: Preferences, storage: Storage | undefined) {
		try {
			const locale = Locale.match(storage?.getItem(Device.#earlier.locale));
			const appearance = storage?.getItem(Device.#earlier.appearance);
			if (!storage || (!locale && !appearance)) return;
			if (storage.getItem(Device.key) === null) {
				preferences.choose({
					...(locale ? { locale } : {}),
					...(appearance === 'light' || appearance === 'dark' ? { appearance } : {})
				});
			}
			storage.removeItem(Device.#earlier.locale);
			storage.removeItem(Device.#earlier.appearance);
		} catch {
			// Storage refused: the earlier choice is simply not carried over
		}
	}
}
